import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { stallsApi } from '@/api/stalls';
import { ApiHttpError } from '@/api/client';
import { StallCard } from '@/components/StallCard';
import { Skeleton } from '@/components/Skeleton';

export function SearchRoute() {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const tid = setTimeout(() => setDebounced(q.trim()), 220);
    return () => clearTimeout(tid);
  }, [q]);

  const { data, isFetching, isError } = useQuery({
    queryKey: ['search', debounced],
    queryFn: ({ signal }) => stallsApi.search(debounced, signal),
    enabled: debounced.length > 0,
    retry: (count, e) => {
      if (e instanceof ApiHttpError && e.status < 500) return false;
      return count < 1;
    },
  });

  const results = data?.stalls ?? [];
  const showEmpty = useMemo(
    // Show the zero-results state whenever the user has typed something and
    // the query has settled — including the error path, so a 503 from the
    // API doesn't trap the user on the trending suggestions forever.
    () => debounced.length > 0 && !isFetching && results.length === 0,
    [debounced, isFetching, results.length],
  );

  return (
    <div className="mx-auto max-w-[720px] px-4 py-4">
      <label
        className="flex min-h-[52px] items-center gap-2 rounded-pill border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-2 shadow-s1"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <input
          type="search"
          autoFocus
          placeholder={t('search.placeholder')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 border-0 bg-transparent text-base outline-none"
        />
      </label>

      <div className="mt-6">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-muted">
          {t('search.trending')}
        </h2>
        <div className="flex flex-wrap gap-2">
          {['boat noodle · ก๋วยเตี๋ยวเรือ', 'moo ping · หมูปิ้ง', 'som tam · ส้มตำ', 'Yaowarat · เยาวราช'].map(
            (term) => (
              <button
                key={term}
                type="button"
                onClick={() => setQ(term.split(' · ')[0] ?? term)}
                className="min-h-[36px] rounded-pill border border-[color:var(--border)] bg-[color:var(--surface)] px-3.5 py-2 text-sm font-semibold text-ink hover:border-soi-400 hover:bg-soi-100"
              >
                {term}
              </button>
            ),
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-2">
        {isFetching &&
          debounced.length > 0 &&
          [1, 2, 3].map((i) => (
            <div key={i} className="rounded-md border border-[color:var(--border)] p-3">
              <div className="grid grid-cols-[56px_1fr] gap-3">
                <Skeleton width={56} height={56} rounded="md" />
                <div className="flex flex-col gap-1.5">
                  <Skeleton width="60%" height={14} />
                  <Skeleton width="90%" height={12} />
                  <Skeleton width="40%" height={12} />
                </div>
              </div>
            </div>
          ))}
        {results.map((s) => (
          <StallCard key={s.id} stall={s} />
        ))}
      </div>

      {showEmpty && (
        <div className="mt-6 rounded-lg border border-dashed border-[color:var(--border)] bg-[color:var(--bg-alt)] p-8 text-center">
          <div className="mb-3 text-5xl" aria-hidden="true">🥢</div>
          <h3 className="m-0 mb-2 text-xl font-bold">{t('search.empty_title')}</h3>
          <p className="m-0 mb-4 text-sm text-ink-soft">{t('search.empty_hint')}</p>
          <Link
            to="/add"
            className="inline-flex min-h-[44px] items-center justify-center rounded-md bg-soi-600 px-4 py-2 text-sm font-semibold text-white"
          >
            {t('search.empty_cta')}
          </Link>
        </div>
      )}

      {isError && debounced.length > 0 && (
        <p className="mt-4 text-sm text-[color:var(--danger)]">{t('errors.offline')}</p>
      )}
    </div>
  );
}
